import React, { useState } from "react"
import DashboardHeader from "../../components/DashboardHeader"
import ExportModal from "../../components/modals/export-modal"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import Spinner from "../../components/Spinner"
import { AiOutlineExport } from "react-icons/ai"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import noReport from "../../assets/noReport.svg"
import { DateRangePickerDropdown } from "../../components/DashboardOverviewFilter"
import { BASE_URL, fetcher } from "../../utils/api"
import useSWR from "swr"
import RenderNoReport from "../../components/NoData/RenderNoReport"
import { isDateWithinRange } from "../../utils/Formatter"

const REPORT_TYPES = [
  { label: "Farmers", value: "farmers" },
  // { label: "Orders", value: "orders" },
  { label: "Harvest Requests", value: "harvest_requests" },
  { label: "Orders", value: "service_orders" },
]
const STATUS_OPTIONS = {
  farmers: ["Available", "Pending", "Responded", "Ordered", "Inactive"],
  orders: ["Delivered", "Pending", "In Transit", "Cancelled", "Confirmed"],
  harvest_requests: ["Pending", "Responded", "Ordered", "Inactive"],
  service_orders: ["Delivered", "Pending", "In Transit", "Cancelled", "Confirmed"],
}


const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

const Index = () => {
  const [selectedReport, setSelectedReport] = useState(REPORT_TYPES[0].value)
  const [status, setStatus] = useState("")
  const [dateFilterActive, setDateFilterActive] = useState(false)
  const [dateRange, setDateRange] = useState(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState("")
  const [states, setStates] = useState({ region: "", district: "", community: "" })
  const [showExportModal, setShowExportModal] = useState(false)


  // Farmer stats
  const { data: farmerReport, isLoading: loadingFarmerReport, error, mutate: mutateFarmer } = useSWR('farmer-reports/', fetcher)

  // harvest-request-reports
  const { data: harvestReport, isLoading: loadingHarvestReport, mutate: mutateHarvest } = useSWR(`harvest-request-reports/`, fetcher)

  // Service orders
  const { data: serviceOrders, isLoading: loadingServiceOrder, mutate: mutateServiceOrders } = useSWR(`service-orders/reports/`, fetcher)

  // Remove DUMMY_DATA and set initial data to empty array
  let data = []

  if (selectedReport === "farmers" && farmerReport?.farmers) {
    data = farmerReport.farmers.map(farmer => ({
      ...farmer,
      id: farmer.id,
      name: farmer.full_name,
      gender: farmer.gender ? farmer.gender.charAt(0).toUpperCase() + farmer.gender.slice(1) : '',
      dob: farmer.dob,
      created_at: farmer.created_at || farmer.datecreated || farmer.date_joined || null,
      phone: farmer.phone,
      email: farmer.email,
      region: farmer.region,
      district: farmer.district,
      community: farmer.community,
      status: farmer.status ? "Available" : "Inactive",
    }))
  }
  if (selectedReport === "service_orders" && serviceOrders?.results) {
    data = serviceOrders.results.map(order => ({
      id: order.marketplace_order_id || order.order_number,
      order_number: order.order_number,
      customer_fullname: order.customer_fullname,
      total_amount: order.total_amount,
      created_at: order.created_at,
      delivery_region: order.delivery_region,
      status: order.status,
    }))
  }
  if (selectedReport === "harvest_requests" && harvestReport?.results) {
    data = harvestReport.results.map(request => ({
      id: request.id,
      request_id: request.request_id,
      title: request.title || '',
      commodity: request.commodity || '',
      owner_firstname: request.owner?.firstname || '',
      owner_lastname: request.owner?.lastname || '',
      created_by: `${request.owner?.firstname || ''} ${request.owner?.lastname || ''}`.trim(),
      owner_region: request.owner?.region || '',
      owner_district: request.owner?.district || '',
      owner_community: request.owner?.community || '',
      price_per_ton: request.price_per_ton,
      weight_in_tons: request.weight_in_tons,
      target_groups_count: request.target_groups_count,
      to_all_farmer: request.to_all_farmer,
      scheduled_publish_date: request.scheduled_publish_date,
      request_status: request.request_status || request.status,
      response_status: request.response_status,
      request_type: request.request_type || request.response_status || '',
      est_no_of_bunches: request.est_no_of_bunches,
      total_estimated_value: request.total_estimated_value,
      created_at: request.created_at,
    }))
  }

  // Improved filter logic for each report type
  const filterRow = (row) => {
    // Status filter
    let statusMatch = true
    if (status) {
      if (selectedReport === "farmers") statusMatch = row.status === status
      else if (selectedReport === "service_orders") statusMatch = row.status === status
      else if (selectedReport === "harvest_requests") statusMatch = row.request_status === status
    }

    // Region/district/community filter (if those fields exist)
    let regionMatch = !states.region || row.region === states.region || row.owner_region === states.region
    let districtMatch = !states.district || row.district === states.district || row.owner_district === states.district
    let communityMatch = !states.community || row.community === states.community || row.owner_community === states.community

    // Search filter (searches multiple fields depending on report type)
    let searchMatch = true
    if (search) {
      const searchLower = search.toLowerCase()
      if (selectedReport === "farmers") {
        searchMatch = row.name?.toLowerCase().includes(searchLower) || row.phone?.includes(search) || row.email?.toLowerCase().includes(searchLower)
      } else if (selectedReport === "service_orders") {
        searchMatch = row.order_number?.toLowerCase().includes(searchLower) || row.customer_fullname?.toLowerCase().includes(searchLower)
      } else if (selectedReport === "harvest_requests") {
        searchMatch =
          row.request_id?.toLowerCase().includes(searchLower) ||
        row.owner_firstname?.toLowerCase().includes(searchLower) ||
        row.owner_lastname?.toLowerCase().includes(searchLower)
      }
    }

    // Date range filter — only applied after user selects dates
    const start = dateFilterActive ? dateRange?.[0]?.startDate : null
    const end = dateFilterActive ? dateRange?.[0]?.endDate : null
    const dateValue = selectedReport === "farmers"
      ? (row.created_at || row.dob)
      : row.created_at
    const inRange = isDateWithinRange(dateValue, start, end)

    return statusMatch && regionMatch && districtMatch && communityMatch && searchMatch && inRange
  }

  const filteredData = data.filter(filterRow)
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const hasActiveFilters = Boolean(
    search || status || states.region || states.district || states.community || dateFilterActive
  )
  const isReportLoading = selectedReport === "farmers"
    ? loadingFarmerReport
    : selectedReport === "harvest_requests"
      ? loadingHarvestReport
      : loadingServiceOrder
  const apiReportData = selectedReport === "farmers"
    ? farmerReport
    : selectedReport === "harvest_requests"
      ? harvestReport
      : serviceOrders

  const summaryCards = isReportLoading
    ? getSummaryCards(selectedReport, apiReportData, true, error)
    : hasActiveFilters
      ? getFilteredSummaryCards(selectedReport, filteredData)
      : getSummaryCards(selectedReport, apiReportData, false, error)

  // For harvest requests table, use paginatedData as paginatedHarvests
  const paginatedHarvests = selectedReport === "harvest_requests" ? paginatedData : []

  // Improved reset filters function
  const resetFilters = () => {
    setStatus("")
    setSearch("")
    setStates({ region: "", district: "", community: "" })
    setCurrentPage(1)
    setDateFilterActive(false)
    setDateRange(null)
  }

  // Handle filter changes
  const handleChange = (e) => {
    setStates({ ...states, [e.target.name]: e.target.value })
    setCurrentPage(1)
  }

  // Guard to ensure dateRange always contains Date objects
  const handleDateRangeChange = (rangeArr) => {
    setDateRange([
      {
        startDate: new Date(rangeArr[0].startDate),
        endDate: new Date(rangeArr[0].endDate),
        key: rangeArr[0].key || "selection"
      }
    ])
    setDateFilterActive(true)
    setCurrentPage(1)
  }

  // UI
  return (
    <div className="w-full lg:px-10 px-2 h-full py-10">
      <div className="w-full space-y-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
          <DashboardHeader label="Reports" />
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
          >
            <AiOutlineExport size={20} />
            Export Report
          </button>
        </div>
        {/* Report Type Dropdown */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div>
            <select
              className="py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              value={selectedReport}
              onChange={e => {
                setSelectedReport(e.target.value)
                mutateFarmer()
                mutateHarvest()
                mutateServiceOrders()
                setCurrentPage(1)
              }}
            >
              {REPORT_TYPES.map(rt => (
                <option key={rt.value} value={rt.value}>{rt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
                setCurrentPage(1)
              }}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              value={status}
              onChange={e => {
                setStatus(e.target.value)
                setCurrentPage(1)
              }}
            >
              <option value="">Select Status</option>
              {STATUS_OPTIONS[selectedReport].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>


        </div>
        {/* Main Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="w-full">
              <FilterWithSearchAndRegion
                placeholder={`Search ${REPORT_TYPES.find(r => r.value === selectedReport)?.label || ""}`}
                suggestions={[]}
                search={search}
                setSearch={setSearch}
                onSearch={val => {
                  setSearch(val)
                  setCurrentPage(1)
                }}
                view={"list"}
                setView={() => { }}
                handleChange={handleChange}
                hideMap={true}
                hideLocationFilters={selectedReport !== "farmers"}
              />
            </div>

            <div className="w-full md:w-fit flex items-end justify-end">
              <DateRangePickerDropdown
                key={dateFilterActive ? 'date-filter-active' : 'date-filter-inactive'}
                range={dateFilterActive ? dateRange : undefined}
                onChange={handleDateRangeChange}
              />
           </div>
          </div>
          {(search || status || states.region || states.district || states.community || dateFilterActive) && (
            <div className="flex md:justify-end -mt-14 lg:mt-0 md:mt-0">
              <button
                onClick={resetFilters}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
        {/* Summary Cards */}
        <div className="flex flex-wrap gap-4 mb-6">
          {summaryCards.map(card => (
            <div key={card.label} className="bg-[#FFF8E1] dark:bg-[#FFF9E8] dark:border-[#FFF9E8] rounded-md px-4 py-2 min-w-[220px] flex-1 border border-black">
              <div className="text-sm font-medium mb-1.5">{card.label}</div>
              <div className="text-base font-semibold">{card.value}</div>
            </div>
          ))}
        </div>
        {/* Table Section */}
        <div className="my-5">
          <div className="flex flex-col">
            <div className="-m-1.5 overflow-x-auto">
              <div className="p-1.5 min-w-full inline-block align-middle">
                <div className="">
                  {loadingFarmerReport || loadingHarvestReport || loadingServiceOrder ? <div className="h-96 grid items-center justify-center">
                    <Spinner />
                  </div> : paginatedData.length > 0 ? (
                      <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-t-lg shadow-sm">
                        <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
                          <ReportsTable reportType={selectedReport} data={paginatedData} />
                        </div>
                      </div>
                  ) : (
                    <RenderNoReport
                      label={REPORT_TYPES?.find((item) => item?.value === selectedReport)?.label?.toLowerCase()}
                      header={REPORT_TYPES?.find((item) => item?.value === selectedReport)?.label}
                    />
                  )}
                </div>
                {/* Pagination */}
                {paginatedData.length > 0 && (
                  <div className="bg-white border-t border-gray-200 p-3 flex flex-row items-center justify-between w-full dark:bg-dark dark:border-border-dark">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300 dark:bg-secondaryDark dark:border-border-dark dark:text-white ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <IoArrowBack size={16} />
                      Previous
                    </button>
                    <div>
                      <ul className="flex flex-row items-center gap-0.5">
                        {Array.from({ length: totalPages }).map((_, index) => (
                          <li key={index}>
                            <button
                              className={`mx-1 flex h-9 w-9 items-center justify-center rounded-full p-0 text-sm text-blue-gray-500 transition duration-150 ease-in-out dark:bg-secondaryDark dark:border-border-dark dark:text-white ${currentPage === index + 1 ? "bg-gray-100" : "bg-transparent"}`}
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className={`px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300 dark:bg-secondaryDark dark:border-border-dark dark:text-white ${(currentPage === totalPages || totalPages === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Next
                      <IoArrowForward size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          onClose={setShowExportModal}
          csvLink={{
            filename: `${selectedReport}_report.csv`,
            headers: getCsvHeaders(selectedReport),
            data: formatExportRows(filteredData, selectedReport)
          }}
          reportType={selectedReport}
        />
      )}
    </div>
  )
}

function getFilteredSummaryCards(reportType, rows) {
  switch (reportType) {
    case "farmers": {
      const males = rows.filter((r) => r.gender?.toLowerCase() === "male").length
      const females = rows.filter((r) => r.gender?.toLowerCase() === "female").length
      return [
        { label: "Total Farmers", value: rows.length },
        { label: "Total Number Of Males", value: males },
        { label: "Total Number Of Females", value: females },
      ]
    }
    case "harvest_requests": {
      const responded = rows.filter((r) =>
        r.request_status?.toLowerCase() === "responded" ||
        r.response_status?.toLowerCase() === "responded"
      ).length
      const pending = rows.filter((r) => r.request_status?.toLowerCase() === "pending").length
      return [
        { label: "Total Requests", value: rows.length },
        { label: "Total Responded", value: responded },
        { label: "Total Pending", value: pending },
      ]
    }
    case "service_orders": {
      const pending = rows.filter((r) => r.status?.toLowerCase() === "pending").length
      const delivered = rows.filter((r) => r.status?.toLowerCase() === "delivered").length
      const totalSales = rows.reduce((sum, r) => sum + (parseFloat(r.total_amount) || 0), 0)
      return [
        { label: "Total Orders", value: rows.length },
        { label: "Total Orders Pending", value: pending },
        { label: "Total Orders Delivered", value: delivered },
        { label: "Total Sales (GHS)", value: totalSales.toLocaleString() },
      ]
    }
    default:
      return []
  }
}

function getSummaryCards(reportType, data, isLoading, error) {
  if (isLoading) {
    return [{ label: "Loading...", value: "Loading..." }]
  }
  if (error) {
    return [{ label: "Error", value: "Failed to load" }]
  }

  switch (reportType) {
    case "farmers":
      return [
        { label: "Total Farmers", value: data?.summary?.total_farmers ?? "N/A" },
        { label: "Total Number Of Males", value: data?.summary?.total_male_farmers ?? "N/A" },
        { label: "Total Number Of Females", value: data?.summary?.total_female_farmers ?? "N/A" },
      ]
    case "harvest_requests":
      return [
        { label: "Total Requests", value: data?.summary?.total_requests ?? "N/A" },
        { label: "Total Responded", value: data?.summary?.total_responded ?? "N/A" },
        { label: "Total Pending", value: data?.summary?.total_pending ?? "N/A" },
      ]
    case "service_orders":
      return [
        { label: "Total Orders", value: data?.statistics?.total_orders ?? "N/A" },
        { label: "Total Orders Pending", value: data?.statistics?.total_pending_orders ?? "N/A" },
        { label: "Total Orders Delivered", value: data?.statistics?.total_delivered_orders ?? "N/A" },
        { label: "Total Sales (GHS)", value: data?.statistics?.total_sales ?? "N/A" },
      ]
    case "orders":
      return [
        { label: "Total Orders", value: 0 },
        { label: "Total Orders Pending", value: 0 },
        { label: "Total Orders Delivered", value: 0 },
        { label: "Total Sales (GHS)", value: 0 },
      ]
    default:
      return []
  }
}

function ReportsTable({ reportType, data }) {
  if (reportType === "farmers") {
    return (
      <table className="min-w-[900px] w-full divide-y divide-gray-200 dark:divide-border-dark">
        <thead className="bg-transparent dark:bg-secondaryDark">
          <tr>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Name</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Gender/DOB</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Phone No./ Email</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Region</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">District</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Community</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
          {data.map((row, idx) => (
            <tr key={row.id} className="text-[13px]">
              <td className="px-6 py-4 whitespace-nowrap font-medium flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-xs font-bold">
                  {row.name.split(" ").map(n => n[0]).join("")}
                </span>
                {row.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">{row.gender} <br /> {row.dob}</td>
              <td className="px-6 py-4 whitespace-nowrap">{row.phone}<br />{row.email}</td>
              <td className="px-6 py-4 whitespace-nowrap">{row.region}</td>
              <td className="px-6 py-4 whitespace-nowrap">{row.district}</td>
              <td className="px-6 py-4 whitespace-nowrap">{row.community}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge status={row.status} type="farmers" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )

  }

  if (reportType === "harvest_requests") {
    return (
      <table className="min-w-[900px] w-full divide-y divide-gray-200 dark:divide-border-dark">
        <thead className="bg-transparent dark:bg-secondaryDark">
          <tr>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Request Id</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Created By</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Price/(Weight/Ton)</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Groups</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Sent/Delivered</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Request Type</th>
            <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
          {data.map((data, index) => (
            <tr key={data.id} className="text-[13px]">
              <td className="px-6 py-4 whitespace-nowrap">{data?.request_id}</td>
              <td className="px-6 py-4 whitespace-nowrap">{`${data?.owner_firstname || ''} ${data?.owner_lastname || ''}`}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                {data?.price_per_ton || data?.total_estimated_value} / {data?.weight_in_tons}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {data?.target_groups_count || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {data?.scheduled_publish_date ? new Date(data.scheduled_publish_date).toLocaleString() : "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {/* Status badge */}
                <span
                  className={`capitalize px-3 py-1 rounded-md text-xs font-semibold 
                    ${data?.request_status?.toLowerCase() === 'published' ? 'bg-yellow-50 text-yellowcolor' :
                      data?.request_status?.toLowerCase() === 'responded' || data?.request_status?.toLowerCase() === 'fulfilled' || data?.request_status?.toLowerCase() === 'received' ? 'bg-green-50 text-green-700' :
                        data?.request_status?.toLowerCase() === 'pending' ? 'bg-blue-50 text-blue-700' :
                          data?.request_status?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-500' :
                            'bg-gray-50 text-gray-500'}
                  `}
                >
                  {data?.request_status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {/* Response status badge */}
                <span
                  className={`capitalize px-3 py-1 rounded-md text-xs font-semibold 
                    ${data?.response_status?.toLowerCase() === 'published' ? 'bg-yellow-50 text-yellowcolor' :
                      data?.response_status?.toLowerCase() === 'responded' || data?.response_status?.toLowerCase() === 'fulfilled' || data?.response_status?.toLowerCase() === 'received' ? 'bg-green-50 text-green-700' :
                        data?.response_status?.toLowerCase() === 'pending' ? 'bg-blue-50 text-blue-700' :
                          data?.response_status?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-500' :
                            'bg-gray-50 text-gray-500'}
                  `}
                >
                  {data?.response_status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                <div className="flex flex-row items-center gap-4">
                  {/* Add your action buttons here if needed */}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  if (reportType === "service_orders") {
    return (
      <table className="min-w-[900px] w-full divide-y divide-gray-200 dark:divide-border-dark">
        <thead className="bg-transparent dark:bg-secondaryDark">
          <tr>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Order ID</th>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Customer Name</th>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Amount(GH₵)</th>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Order Date</th>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Location</th>
            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
          {data?.length > 0 ? data.map((order, index) => (
            <tr key={order?.id} className="text-[13px]">
              <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-gray-100">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.41712 1.25C6.69712 0.66 7.30012 0.25 7.99912 0.25H11.9991C12.6971 0.25 13.3001 0.66 13.5811 1.25C14.2641 1.256 14.7971 1.287 15.2731 1.473C15.8413 1.69527 16.3355 2.07301 16.6991 2.563C17.0661 3.056 17.2381 3.69 17.4751 4.561L17.5121 4.697L18.1021 6.864C18.4955 7.06248 18.8403 7.34518 19.1121 7.692C19.7341 8.489 19.8441 9.438 19.7341 10.526C19.6261 11.582 19.2941 12.912 18.8781 14.577L18.8511 14.682C18.5881 15.735 18.3741 16.59 18.1211 17.257C17.8551 17.953 17.5191 18.523 16.9641 18.956C16.4101 19.389 15.7751 19.576 15.0361 19.666C14.3271 19.75 13.4461 19.75 12.3611 19.75H7.63712C6.55212 19.75 5.67012 19.75 4.96212 19.665C4.22212 19.577 3.58812 19.389 3.03312 18.955C2.47912 18.523 2.14312 17.953 1.87712 17.257C1.62312 16.59 1.41012 15.735 1.14712 14.682L1.12012 14.577C0.704122 12.912 0.371122 11.582 0.264122 10.527C0.154122 9.437 0.264122 8.489 0.885122 7.692C1.16812 7.33 1.50712 7.062 1.89512 6.864L2.48512 4.697L2.52312 4.561C2.76012 3.69 2.93212 3.056 3.29912 2.562C3.66285 2.07238 4.15702 1.695 4.72512 1.473C5.20112 1.287 5.73312 1.255 6.41712 1.25ZM6.41812 2.753C5.75612 2.76 5.49112 2.785 5.27112 2.871C4.96509 2.99068 4.69893 3.19411 4.50312 3.458C4.32712 3.695 4.22312 4.026 3.93312 5.093L3.57912 6.389C4.61712 6.25 5.95712 6.25 7.62112 6.25H12.3761C14.0411 6.25 15.3811 6.25 16.4181 6.389L16.0651 5.092C15.7741 4.025 15.6711 3.694 15.4951 3.457C15.2993 3.19311 15.0332 2.98968 14.7271 2.87C14.5071 2.784 14.2411 2.759 13.5791 2.752C13.437 3.05066 13.2131 3.30293 12.9335 3.47956C12.6539 3.6562 12.3299 3.74996 11.9991 3.75H7.99912C7.66837 3.74996 7.34439 3.6562 7.06475 3.47956C6.78511 3.30293 6.56125 3.05066 6.41912 2.752M7.99912 1.75C7.93282 1.75 7.86923 1.77634 7.82235 1.82322C7.77546 1.87011 7.74912 1.9337 7.74912 2C7.74912 2.0663 7.77546 2.12989 7.82235 2.17678C7.86923 2.22366 7.93282 2.25 7.99912 2.25H11.9991C12.0654 2.25 12.129 2.22366 12.1759 2.17678C12.2228 2.12989 12.2491 2.0663 12.2491 2C12.2491 1.9337 12.2228 1.87011 12.1759 1.82322C12.129 1.77634 12.0654 1.75 11.9991 1.75H7.99912ZM3.69912 7.886C2.78912 8.018 2.34812 8.258 2.06912 8.616C1.78912 8.973 1.66412 9.458 1.75712 10.374C1.85212 11.31 2.15712 12.534 2.59112 14.274C2.86912 15.382 3.06112 16.15 3.28112 16.724C3.49112 17.278 3.69812 17.571 3.95812 17.774C4.21712 17.976 4.55112 18.105 5.14112 18.176C5.75112 18.249 6.54112 18.25 7.68512 18.25H12.3151C13.4581 18.25 14.2501 18.249 14.8591 18.176C15.4491 18.106 15.7831 17.976 16.0421 17.774C16.3021 17.571 16.5081 17.278 16.7201 16.724C16.9381 16.15 17.1311 15.382 17.4081 14.274C17.8431 12.534 18.1481 11.31 18.2421 10.374C18.3361 9.458 18.2101 8.972 17.9311 8.615C17.6521 8.258 17.2111 8.018 16.3001 7.886C15.3701 7.752 14.1081 7.75 12.3151 7.75H7.68512C5.89212 7.75 4.63012 7.752 3.70012 7.886" fill="#FFB733" />
                  </svg>
                </div>
                {order?.order_number || order?.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {order?.customer_fullname}
              </td>
              <td className="px-6 py-4 whitespace-nowrap font-light">
                GH₵ {parseFloat(order?.total_amount).toLocaleString()}
              </td>
              <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                {order?.created_at ? new Date(order?.created_at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : ''}
              </td>
              <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                {order?.delivery_region}
              </td>
              <td className="px-6 py-4 capitalize whitespace-nowrap text-end font-light">
                {order?.status}
              </td>
            </tr>
          )) : (
            <tr><td colSpan={7} className="text-center py-8 text-gray-400">No orders found.</td></tr>
          )}
        </tbody>
      </table>
    )
  }
  return null
}

const formatExportValue = (value) => {
  if (value === undefined || value === null || value === '') return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const formatExportRows = (rows, reportType) =>
  rows.map((row) => {
    const headers = getCsvHeaders(reportType)
    const exportRow = {}
    headers.forEach(({ key }) => {
      let value = row[key]
      if ((key === 'created_at' || key === 'dob' || key === 'scheduled_publish_date') && value) {
        const parsed = new Date(value)
        value = Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
      }
      exportRow[key] = formatExportValue(value)
    })
    return exportRow
  })

function getCsvHeaders(reportType) {
  switch (reportType) {
    case "farmers":
      return [
        { label: "ID", key: "id" },
        { label: "Name", key: "name" },
        { label: "Gender", key: "gender" },
        { label: "Date of Birth", key: "dob" },
        { label: "Phone", key: "phone" },
        { label: "Email", key: "email" },
        { label: "Region", key: "region" },
        { label: "District", key: "district" },
        { label: "Community", key: "community" },
        { label: "Status", key: "status" },
        { label: "Created At", key: "created_at" },
      ]
    case "harvest_requests":
      return [
        { label: "Request ID", key: "request_id" },
        { label: "Title", key: "title" },
        { label: "Commodity", key: "commodity" },
        { label: "Created By", key: "created_by" },
        { label: "Owner First Name", key: "owner_firstname" },
        { label: "Owner Last Name", key: "owner_lastname" },
        { label: "Owner Region", key: "owner_region" },
        { label: "Owner District", key: "owner_district" },
        { label: "Owner Community", key: "owner_community" },
        { label: "Price per Ton", key: "price_per_ton" },
        { label: "Weight in Tons", key: "weight_in_tons" },
        { label: "Total Estimated Value", key: "total_estimated_value" },
        { label: "Target Groups Count", key: "target_groups_count" },
        { label: "To All Farmers", key: "to_all_farmer" },
        { label: "Scheduled Publish Date", key: "scheduled_publish_date" },
        { label: "Request Status", key: "request_status" },
        { label: "Response Status", key: "response_status" },
        { label: "Request Type", key: "request_type" },
        { label: "Est. Number of Bunches", key: "est_no_of_bunches" },
        { label: "Created At", key: "created_at" },
      ]
    case "service_orders":
      return [
        { label: "Order ID", key: "id" },
        { label: "Order Number", key: "order_number" },
        { label: "Customer Name", key: "customer_fullname" },
        { label: "Total Amount (GHS)", key: "total_amount" },
        { label: "Order Date", key: "created_at" },
        { label: "Delivery Region", key: "delivery_region" },
        { label: "Status", key: "status" },
      ]
    case "orders":
      return [
        { label: "Order ID", key: "id" },
        { label: "Status", key: "status" },
        { label: "Amount", key: "amount" },
        { label: "Created Date", key: "created_at" },
      ]
    default:
      return []
  }
}

function StatusBadge({ status, type }) {
  let color = "bg-gray-200 text-gray-700"
  if (type === "farmers") {
    if (status === "Available") color = "bg-green-100 text-green-700"
    else if (status === "Inactive") color = "bg-blue-100 text-blue-700"
    else if (status === "Pending") color = "bg-yellow-100 text-yellow-700"
    else if (status === "Responded") color = "bg-green-100 text-green-700"
    else if (status === "Ordered") color = "bg-blue-100 text-blue-700"
  }
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${color}`}>{status}</span>
  )
}

export default Index